import PropTypes from 'prop-types';
import React from 'react';
import routePath, {Routes} from '@account/utils/route';
import {Box, Link, Stack, Tooltip, Typography} from '@mui/material';
import {Link as RouterLink} from 'react-router-dom';
import {alpha} from '@mui/material/styles';
import {buildEntitlementBar} from './entitlementBar';
import {displayDate, displayDateTime} from '@account/utils/dates';
import {renderQuota} from '@account/utils/quota';

const OPERATION_LABELS = {
  PROVISION: 'Provisioned',
  ALLOCATE: 'Allocated',
  RECALL: 'Recalled',
  ADJUSTMENT: 'Adjustment',
  CANCELLATION: 'Cancellation',
  REVERSAL: 'Reversal',
  EXPIRED: 'Expired',
};

// Rail dot color per operation — mirrors the Amount column semantics in the
// table view (provision = credit, allocate = neutral move, expired = drained).
const dotColor = (theme, type) =>
  ({
    PROVISION: theme.palette.success.main,
    ALLOCATE: theme.palette.primary.main,
    RECALL: theme.palette.warning.main,
    ADJUSTMENT: theme.palette.info.main,
    CANCELLATION: theme.palette.error.main,
    REVERSAL: theme.palette.error.main,
    EXPIRED: theme.palette.text.disabled,
  })[type] ?? theme.palette.text.secondary;

const actorName = createdBy => {
  if (!createdBy) return 'System';
  if (typeof createdBy === 'string') return createdBy;
  if (createdBy.type === 'system') return 'System';
  if (createdBy.type === 'hidden') return 'Hidden';
  return createdBy.name ?? 'System';
};

// The signed magnitude phrase for an event's title, e.g. "+20,000 tiles".
const amountPhrase = row => {
  const {operation_type, amount, quota_units} = row;
  if (amount === null || amount === undefined) return null;
  const magnitude = renderQuota(Math.abs(Number(amount)), quota_units);
  if (operation_type === 'PROVISION') return `+${magnitude}`;
  if (operation_type === 'ALLOCATE' || operation_type === 'EXPIRED')
    return `−${magnitude}`;
  return Number(amount) >= 0 ? `+${magnitude}` : `−${magnitude}`;
};

const WorkspaceLink = ({row, customerAccountId}) => {
  const {destination_workspace_id, destination_workspace_name} = row;
  if (!destination_workspace_id) return null;
  const label = destination_workspace_name ?? destination_workspace_id;
  return customerAccountId ? (
    <Link
      component={RouterLink}
      to={routePath(Routes.WORKSPACE_DETAIL, {
        accountId: customerAccountId,
        workspaceId: destination_workspace_id,
      })}
      variant="body2"
      underline="hover"
    >
      {label}
    </Link>
  ) : (
    <Link
      href={`https://insights.planet.com/account/#/workspaces/${destination_workspace_id}/info`}
      target="_blank"
      rel="noopener noreferrer"
      variant="body2"
      underline="hover"
    >
      {label}
    </Link>
  );
};

WorkspaceLink.propTypes = {
  row: PropTypes.object.isRequired,
  customerAccountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// A single legend entry: a colored swatch matching a bar fill + its label.
const LegendItem = ({sx, label}) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <Box
      sx={theme => ({
        width: 12,
        height: 12,
        borderRadius: 0.5,
        ...sx(theme),
      })}
    />
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Stack>
);

LegendItem.propTypes = {
  sx: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

// EntitlementBar: one horizontal bar for the whole CA-level provision window,
// with each allocation carved out as a band and each boundary marked. Makes the
// "one entitlement split by allocation events" story visible at a glance.
const EntitlementBar = ({bar}) => {
  if (!bar.valid) return null;
  return (
    <Box sx={{mb: 3}}>
      <Stack direction="row" justifyContent="space-between" sx={{mb: 0.5}}>
        <Typography variant="caption" color="text.secondary">
          {displayDate(new Date(bar.start))}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {displayDate(new Date(bar.end))}
        </Typography>
      </Stack>
      <Box
        sx={theme => ({
          position: 'relative',
          height: 28,
          borderRadius: 1,
          // Base = provisioned-but-held quota. Same green as the "Provisioned"
          // rail dot so the bar reads in the timeline's color language.
          bgcolor: alpha(theme.palette.success.main, 0.22),
          border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
          overflow: 'hidden',
        })}
      >
        {bar.bands.map(band => (
          <Tooltip
            key={`${band.fromMs}-${band.toMs}-${band.workspaceName ?? ''}`}
            title={
              band.workspaceName
                ? `Allocated to ${band.workspaceName}`
                : 'Allocated to a workspace'
            }
          >
            <Box
              sx={theme => ({
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${band.fromFrac * 100}%`,
                width: `${Math.max(0.5, (band.toFrac - band.fromFrac) * 100)}%`,
                // Allocated portion — same blue as the "Allocated" rail dot.
                bgcolor: alpha(theme.palette.primary.main, 0.85),
              })}
            />
          </Tooltip>
        ))}
        {/* Ticks mark each interior allocation boundary on the bar itself. */}
        {bar.markers.map(m => (
          <Box
            key={`tick-${m.ms}`}
            sx={theme => ({
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${m.frac * 100}%`,
              width: '2px',
              bgcolor: theme.palette.text.primary,
              opacity: 0.5,
            })}
          />
        ))}
      </Box>
      {/* Boundary date labels sit below the bar, edge-aware so they never
          overflow the container when a boundary lands near an end. */}
      <Box sx={{position: 'relative', height: 18, mt: 0.25}}>
        {bar.markers.map(m => {
          const nearStart = m.frac <= 0.05;
          const nearEnd = m.frac >= 0.95;
          const position = nearStart
            ? {left: 0}
            : nearEnd
              ? {left: '100%', transform: 'translateX(-100%)'}
              : {left: `${m.frac * 100}%`, transform: 'translateX(-50%)'};
          return (
            <Box
              key={m.ms}
              sx={{position: 'absolute', whiteSpace: 'nowrap', ...position}}
            >
              <Typography variant="caption" color="text.secondary">
                {displayDate(new Date(m.ms))}
              </Typography>
            </Box>
          );
        })}
      </Box>
      {/* Legend ties the bar's fills to the timeline's color language. */}
      <Stack direction="row" spacing={2} sx={{mt: 0.5}}>
        <LegendItem
          sx={theme => ({
            bgcolor: alpha(theme.palette.success.main, 0.22),
            border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
          })}
          label="Provisioned (held)"
        />
        <LegendItem
          sx={theme => ({bgcolor: alpha(theme.palette.primary.main, 0.85)})}
          label="Allocated"
        />
      </Stack>
    </Box>
  );
};

EntitlementBar.propTypes = {bar: PropTypes.object.isRequired};

// LedgerTimeline renders the grouped ledger feed as a vertical event timeline
// (Provisioned … / Allocated … → workspace …) beneath an entitlement bar that
// groups the contiguous CA terms and marks each allocation boundary.
const LedgerTimeline = ({rows = [], customerAccountId}) => {
  if (rows.length === 0) {
    return (
      <Typography color="text.secondary" variant="body2" sx={{p: 2}}>
        No quota history recorded. This product may predate the ledger system.
      </Typography>
    );
  }

  const bar = buildEntitlementBar(rows);
  // Oldest-first: matches the Table view's order (consistent across the toggle)
  // and the left→right time axis of the entitlement bar above, so the bar and
  // the feed read as one continuous lifecycle instead of fighting each other.
  const events = [...rows].sort((a, b) =>
    (a.created_at ?? '').localeCompare(b.created_at ?? '')
  );

  return (
    <Box>
      <EntitlementBar bar={bar} />

      <Stack>
        {events.map((row, i) => {
          const isLast = i === events.length - 1;
          const label =
            OPERATION_LABELS[row.operation_type] ?? row.operation_type;
          const amount = amountPhrase(row);
          const isAllocate = row.operation_type === 'ALLOCATE';
          return (
            <Stack
              key={
                row.operation_id ??
                `${row.operation_type}-${row.created_at}-${row.valid_from}`
              }
              direction="row"
              spacing={1.5}
            >
              {/* Rail: connecting line + colored dot. */}
              <Box sx={{position: 'relative', width: 16, flexShrink: 0}}>
                {!isLast && (
                  <Box
                    sx={theme => ({
                      position: 'absolute',
                      left: 7,
                      top: 8,
                      bottom: -8,
                      width: 2,
                      bgcolor: theme.palette.divider,
                    })}
                  />
                )}
                <Box
                  sx={theme => ({
                    position: 'absolute',
                    left: 2,
                    top: 4,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: dotColor(theme, row.operation_type),
                  })}
                />
              </Box>

              <Box sx={{pb: 3, minWidth: 0}}>
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="baseline"
                  flexWrap="wrap"
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    data-testid="timeline-event-label"
                  >
                    {label}
                  </Typography>
                  {amount && (
                    <Typography
                      variant="body2"
                      sx={{fontVariantNumeric: 'tabular-nums'}}
                    >
                      {amount}
                    </Typography>
                  )}
                  {isAllocate && row.destination_workspace_id && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        →
                      </Typography>
                      <WorkspaceLink
                        row={row}
                        customerAccountId={customerAccountId}
                      />
                    </>
                  )}
                </Stack>
                {(row.valid_from || row.valid_to) && (
                  <Typography variant="caption" color="text.secondary">
                    {displayDate(row.valid_from)} – {displayDate(row.valid_to)}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {displayDateTime(row.created_at)} ·{' '}
                  {actorName(row.created_by)}
                </Typography>
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

LedgerTimeline.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  customerAccountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default LedgerTimeline;
